import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  Inbox, Mail, MailOpen, CheckCheck, Trash2, Filter,
  Bell, Mailbox, Sparkles, ShoppingBag, Package, AlertTriangle, Cog, ChevronRight,
  Archive, ArchiveRestore, MessageCircle, MessageSquare, Shield
} from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import axiosInstance from '../../api/axios';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';
import { de } from 'date-fns/locale';

const TYPE_META = {
  newsletter_approval: { icon: Mail,         label: 'Newsletter',    color: '#8B7355' },
  ai_suggestion:       { icon: Sparkles,     label: 'KI-Vorschlag',  color: '#C4A084' },
  new_order:           { icon: ShoppingBag,  label: 'Bestellung',    color: '#16a34a' },
  support_request:     { icon: MessageCircle,label: 'Chat',          color: '#F0AD4E' },
  blog_answer:         { icon: MessageSquare,label: 'Blog-Antwort',  color: '#7C5BCF' },
  legal_update:        { icon: Shield,       label: 'Rechtliches',   color: '#dc2626' },
  low_stock:           { icon: AlertTriangle,label: 'Niedriger Bestand', color: '#dc2626' },
  system:              { icon: Cog,          label: 'System',        color: '#6b7280' },
};

function typeMeta(type) {
  return TYPE_META[type] || { icon: Inbox, label: type || 'Hinweis', color: '#6b7280' };
}

const AdminInbox = () => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [view, setView] = useState('inbox'); // 'inbox' | 'archive'
  const [unreadCount, setUnreadCount] = useState(0);
  const [archivedCount, setArchivedCount] = useState(0);

  const fetchInbox = useCallback(async () => {
    try {
      setLoading(true);
      const params = { archived: view === 'archive' };
      if (filter === 'unread') params.unread_only = true;
      else if (filter !== 'all') params.type_filter = filter;
      const res = await axiosInstance.get('/admin-inbox/', { params });
      setNotifications(res.data.notifications || []);
      setUnreadCount(res.data.unread_count || 0);
      setArchivedCount(res.data.archived_count || 0);
    } catch (e) {
      toast.error('Postfach konnte nicht geladen werden');
    } finally {
      setLoading(false);
    }
  }, [filter, view]);

  useEffect(() => { fetchInbox(); }, [fetchInbox]);

  const handleMarkRead = async (id) => {
    try {
      await axiosInstance.put(`/admin-inbox/${id}/read`);
      fetchInbox();
    } catch (e) {
      toast.error('Aktualisieren fehlgeschlagen');
    }
  };

  const handleMarkUnread = async (id) => {
    try {
      await axiosInstance.put(`/admin-inbox/${id}/unread`);
      fetchInbox();
    } catch (e) {
      toast.error('Aktualisieren fehlgeschlagen');
    }
  };

  const handleMarkAllRead = async () => {
    try {
      const res = await axiosInstance.put('/admin-inbox/mark-all-read');
      toast.success(`${res.data.updated} Nachricht(en) als gelesen markiert`);
      fetchInbox();
    } catch (e) {
      toast.error('Aktion fehlgeschlagen');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Diese Nachricht wirklich löschen?')) return;
    try {
      await axiosInstance.delete(`/admin-inbox/${id}`);
      fetchInbox();
    } catch (e) {
      toast.error('Löschen fehlgeschlagen');
    }
  };

  const handleArchive = async (id) => {
    try {
      await axiosInstance.put(`/admin-inbox/${id}/archive`);
      toast.success('Ins Archiv verschoben');
      fetchInbox();
    } catch (e) {
      toast.error('Archivieren fehlgeschlagen');
    }
  };

  const handleUnarchive = async (id) => {
    try {
      await axiosInstance.put(`/admin-inbox/${id}/unarchive`);
      toast.success('Aus Archiv zurückgeholt');
      fetchInbox();
    } catch (e) {
      toast.error('Aktion fehlgeschlagen');
    }
  };

  const formatTime = (iso) => {
    try {
      return formatDistanceToNow(new Date(iso), { addSuffix: true, locale: de });
    } catch (_) {
      return iso;
    }
  };

  const filters = [
    { key: 'all',                 label: 'Alle',          icon: Inbox },
    { key: 'unread',              label: 'Ungelesen',     icon: Mail },
    { key: 'blog_answer',         label: 'Blog-Antworten',icon: MessageSquare },
    { key: 'support_request',     label: 'Chat',          icon: MessageCircle },
    { key: 'newsletter_approval', label: 'Newsletter',    icon: Mail },
    { key: 'ai_suggestion',       label: 'KI',            icon: Sparkles },
    { key: 'new_order',           label: 'Bestellungen',  icon: ShoppingBag },
    { key: 'legal_update',        label: 'Rechtliches',   icon: Shield },
    { key: 'low_stock',           label: 'Bestand',       icon: AlertTriangle },
  ];

  return (
    <div className="min-h-screen p-3 sm:p-6" style={{ backgroundColor: '#F5EDE4' }} data-testid="admin-inbox-page">
      <div className="max-w-4xl mx-auto space-y-4">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-[#E8DCC8]">
              <Mailbox className="h-6 w-6 text-[#5D4E3C]" />
            </div>
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-[#5D4E3C]">Admin-Postfach</h1>
              <p className="text-xs sm:text-sm text-[#8B7355]">
                {unreadCount > 0
                  ? `${unreadCount} ungelesene Nachricht${unreadCount === 1 ? '' : 'en'}`
                  : 'Alle Nachrichten gelesen ✓'}
              </p>
            </div>
          </div>
          {unreadCount > 0 && (
            <Button
              variant="outline"
              onClick={handleMarkAllRead}
              className="rounded-none border-[#C4A084] text-[#5D4E3C] hover:bg-[#E8DCC8] w-full sm:w-auto"
              data-testid="inbox-mark-all-read-btn"
            >
              <CheckCheck className="h-4 w-4 mr-2" />
              Alle als gelesen
            </Button>
          )}
        </div>

        {/* Inbox ↔ Archive view tabs */}
        <div className="flex border border-[#D4C4B0] bg-white" data-testid="inbox-view-tabs">
          <button
            type="button"
            onClick={() => setView('inbox')}
            className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium transition-colors ${
              view === 'inbox' ? 'bg-[#5D4E3C] text-white' : 'text-[#5D4E3C] hover:bg-[#F5EDE4]'
            }`}
            data-testid="inbox-view-tab-inbox"
          >
            <Inbox className="h-4 w-4" /> Posteingang
            {unreadCount > 0 && view !== 'inbox' && (
              <span className="ml-1 inline-flex items-center justify-center text-[10px] font-bold bg-red-500 text-white rounded-full h-4 min-w-[16px] px-1">
                {unreadCount}
              </span>
            )}
          </button>
          <button
            type="button"
            onClick={() => setView('archive')}
            className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium transition-colors border-l border-[#D4C4B0] ${
              view === 'archive' ? 'bg-[#5D4E3C] text-white' : 'text-[#5D4E3C] hover:bg-[#F5EDE4]'
            }`}
            data-testid="inbox-view-tab-archive"
          >
            <Archive className="h-4 w-4" /> Archiv ({archivedCount})
          </button>
        </div>

        {/* Filters */}
        <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1" data-testid="inbox-filters">
          {filters.map((f) => {
            const Icon = f.icon;
            const active = filter === f.key;
            return (
              <button
                key={f.key}
                onClick={() => setFilter(f.key)}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium whitespace-nowrap border transition-colors ${
                  active
                    ? 'bg-[#5D4E3C] text-white border-[#5D4E3C]'
                    : 'bg-white text-[#5D4E3C] border-[#D4C4B0] hover:bg-[#F5F0E8]'
                }`}
                data-testid={`inbox-filter-${f.key}`}
              >
                <Icon className="h-3.5 w-3.5" />
                {f.label}
              </button>
            );
          })}
        </div>

        {/* List */}
        <Card className="rounded-none border-[#D4C4B0]">
          <CardContent className="p-0">
            {loading ? (
              <div className="text-center py-16 text-[#8B7355]" data-testid="inbox-loading">
                Lädt…
              </div>
            ) : notifications.length === 0 ? (
              <div className="text-center py-16 text-[#8B7355]" data-testid="inbox-empty">
                <Inbox className="h-12 w-12 mx-auto mb-3 opacity-40" />
                <p>Keine Nachrichten in diesem Bereich</p>
              </div>
            ) : (
              <ul className="divide-y divide-[#E8DCC8]">
                {notifications.map((n) => {
                  const meta = typeMeta(n.type);
                  const Icon = meta.icon;
                  return (
                    <li
                      key={n.id}
                      className={`flex flex-col sm:flex-row sm:items-start gap-3 p-4 transition-colors ${
                        !n.read ? 'bg-[#FDF8F0]' : 'bg-white hover:bg-[#FAF5EE]'
                      }`}
                      data-testid={`inbox-item-${n.id}`}
                    >
                      <div
                        className="flex-shrink-0 w-10 h-10 flex items-center justify-center"
                        style={{ backgroundColor: `${meta.color}20`, color: meta.color }}
                      >
                        <Icon className="h-5 w-5" />
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2 mb-1">
                          <Badge
                            variant="outline"
                            className="text-[10px] rounded-none border-[#D4C4B0]"
                            style={{ color: meta.color }}
                          >
                            {meta.label}
                          </Badge>
                          {!n.read && (
                            <span className="text-[10px] px-1.5 py-0.5 bg-[#C4A084] text-white">NEU</span>
                          )}
                          <span className="text-[11px] text-[#A89878]">{formatTime(n.created_at)}</span>
                        </div>

                        <p className={`text-sm ${!n.read ? 'font-semibold text-[#5D4E3C]' : 'text-[#6D5A47]'}`}>
                          {n.title}
                        </p>
                        <p className="text-sm text-[#8B7355] mt-0.5 break-words">{n.message}</p>

                        {n.link && (
                          <Link
                            to={n.link}
                            className="inline-flex items-center gap-1 mt-2 text-xs text-[#8B7355] hover:text-[#5D4E3C] underline"
                            data-testid={`inbox-item-link-${n.id}`}
                          >
                            {n.link_text || 'Öffnen'} <ChevronRight className="h-3 w-3" />
                          </Link>
                        )}
                      </div>

                      <div className="flex gap-2 sm:flex-col flex-shrink-0">
                        {n.read ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleMarkUnread(n.id)}
                            className="rounded-none h-8 w-8 p-0"
                            title="Als ungelesen markieren"
                            data-testid={`inbox-mark-unread-${n.id}`}
                          >
                            <Mail className="h-4 w-4" />
                          </Button>
                        ) : (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleMarkRead(n.id)}
                            className="rounded-none h-8 w-8 p-0"
                            title="Als gelesen markieren"
                            data-testid={`inbox-mark-read-${n.id}`}
                          >
                            <MailOpen className="h-4 w-4" />
                          </Button>
                        )}
                        {view === 'archive' ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleUnarchive(n.id)}
                            className="rounded-none h-8 w-8 p-0 text-[#5D4E3C]"
                            title="Aus Archiv zurückholen"
                            data-testid={`inbox-unarchive-${n.id}`}
                          >
                            <ArchiveRestore className="h-4 w-4" />
                          </Button>
                        ) : (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleArchive(n.id)}
                            className="rounded-none h-8 w-8 p-0 text-[#5D4E3C]"
                            title="Archivieren (statt löschen)"
                            data-testid={`inbox-archive-${n.id}`}
                          >
                            <Archive className="h-4 w-4" />
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(n.id)}
                          className="rounded-none h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                          title="Löschen"
                          data-testid={`inbox-delete-${n.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminInbox;
